import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangePicker } from './ui/date-range-picker';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import {
  PERIOD_MODES, QUICK_PICKS, addDays, businessToday, dateToIso, describeResolved, financialYearMonths, formatMonth,
  formatMonthList, formatRange, isoToDate, recentMonths, sortMonths,
} from '../lib/period';

const ResolvedBadge = ({ resolved }) => {
  const label = describeResolved(resolved);
  if (!label) return null;
  const custom = resolved.period_source !== 'DEFAULT_MTD';
  return (
    <span
      className={`px-2 py-1 rounded-md border text-[11px] whitespace-nowrap ${custom ? 'border-amber-500/40 bg-amber-500/10 text-amber-300' : 'border-border bg-muted/30 text-muted-foreground'}`}
      data-testid="period-resolved-badge"
      title={`Period applied by the server (${resolved.period_timezone || 'Asia/Kolkata'} business dates)`}
    >
      {label}
    </span>
  );
};

const RangeToolbar = ({ state, mode, resolved }) => {
  const { period, setPreset, setRange, setAllTime, reset, allTimeUnsupported } = state;
  const selectValue = period.allTime ? 'all_time' : (period.preset || 'custom');
  const onPick = (v) => {
    if (v === 'all_time') setAllTime();
    else if (v !== 'custom') setPreset(v);
  };
  const onRange = (r) => {
    if (!r?.from) { reset(); return; }
    const from = dateToIso(r.from);
    setRange(from, r.to ? dateToIso(r.to) : from);
  };
  const pickerRange = period.allTime ? { from: undefined, to: undefined } : { from: isoToDate(period.from), to: isoToDate(period.to) };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="period-toolbar">
      <Select value={selectValue} onValueChange={onPick}>
        <SelectTrigger className="w-[210px]" data-testid="period-quick-pick">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {QUICK_PICKS.map((p) => (
            <SelectItem key={p.value} value={p.value} data-testid={`period-pick-${p.value}`}>{p.label}</SelectItem>
          ))}
          <SelectItem value="custom" data-testid="period-pick-custom">Custom</SelectItem>
          {mode === PERIOD_MODES.RANGE_ALL_TIME && (
            <SelectItem value="all_time" data-testid="period-pick-all_time">All time</SelectItem>
          )}
        </SelectContent>
      </Select>
      <DateRangePicker dateRange={pickerRange} onDateRangeChange={onRange} placeholder="All time" />
      {allTimeUnsupported && (
        <span className="text-[11px] text-muted-foreground" data-testid="period-all-time-unsupported">
          All time isn't available here · showing month to date
        </span>
      )}
      <ResolvedBadge resolved={resolved} />
    </div>
  );
};

const MonthSelectionToolbar = ({ state, resolved }) => {
  const { shared, months, monthsAreExplicit, setMonths } = state;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(months);
  const options = recentMonths();
  months.forEach((m) => { if (!options.some((o) => o.value === m)) options.push({ value: m, label: formatMonth(m) }); });
  options.sort((x, y) => (x.value < y.value ? 1 : -1));

  const onOpenChange = (o) => { setOpen(o); if (o) setDraft(months); };
  const toggle = (m) => setDraft((d) => (d.includes(m) ? d.filter((x) => x !== m) : sortMonths([...d, m])));
  const apply = () => { if (draft.length) { setMonths(draft); setOpen(false); } };
  const thisMonth = businessToday().slice(0, 7);
  const lastMonth = addDays(`${thisMonth}-01`, -1).slice(0, 7);
  const shortcuts = [
    { id: 'select-all', label: 'Select all', months: options.map((o) => o.value) },
    { id: 'clear', label: 'Clear', months: [] },
    { id: 'this-month', label: 'This month', months: [thisMonth] },
    { id: 'last-month', label: 'Last month', months: [lastMonth] },
    { id: 'this-fy', label: 'This financial year', months: financialYearMonths(0) },
    { id: 'last-fy', label: 'Last financial year', months: financialYearMonths(-1) },
  ];

  const note = monthsAreExplicit || shared.allTime
    ? `Tax works by whole filing month · ${months.length} selected`
    : `Tax works by filing month — your range ${formatRange(shared.from, shared.to)} is not used literally; pre-checked whole month${months.length > 1 ? 's' : ''}: ${formatMonthList(months)}`;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="period-toolbar-months">
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="min-w-[200px] justify-between" data-testid="period-months-trigger">
            <span className="truncate max-w-[320px]">{formatMonthList(months)}</span>
            <span className="ml-2 text-[10px] text-muted-foreground">{months.length} mo</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-3" align="start" data-testid="period-months-popover">
          <div className="flex flex-wrap gap-1 mb-2">
            {shortcuts.map((sc) => (
              <button key={sc.id} type="button" onClick={() => setDraft(sortMonths(sc.months))}
                className="px-2 py-0.5 text-[11px] rounded border border-border hover:bg-muted/40" data-testid={`period-months-${sc.id}`}>
                {sc.label}
              </button>
            ))}
          </div>
          <div className="max-h-[260px] overflow-y-auto space-y-1 pr-1">
            {options.map((o) => (
              <label key={o.value} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted/30 cursor-pointer text-sm">
                <Checkbox checked={draft.includes(o.value)} onCheckedChange={() => toggle(o.value)} data-testid={`period-month-checkbox-${o.value}`} />
                {o.label}
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
            <span className={`text-[11px] ${draft.length ? 'text-muted-foreground' : 'text-amber-300'}`} data-testid="period-months-draft-count">
              {draft.length ? `${draft.length} month${draft.length > 1 ? 's' : ''} selected` : 'Select at least one month'}
            </span>
            <Button size="sm" onClick={apply} disabled={!draft.length} data-testid="period-months-apply">Apply</Button>
          </div>
        </PopoverContent>
      </Popover>
      <span className={`text-[11px] max-w-[460px] ${monthsAreExplicit ? 'text-muted-foreground' : 'text-amber-300'}`} data-testid="period-month-note">{note}</span>
      <ResolvedBadge resolved={resolved} />
    </div>
  );
};

/** Shown on tax pages when more than one month is selected. */
export const FilingActionsNotice = ({ months }) => (months.length > 1 ? (
  <div className="text-xs px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/5 text-amber-300" data-testid="filing-actions-single-month-notice">
    Filing actions are available for a single month only.
  </div>
) : null);

/** One toolbar for every Accounts page. `state` = usePeriod(mode); `resolved` = server period metadata. */
export const PeriodToolbar = ({ state, mode = PERIOD_MODES.RANGE, resolved }) => (
  mode === PERIOD_MODES.MONTH_SELECTION
    ? <MonthSelectionToolbar state={state} resolved={resolved} />
    : <RangeToolbar state={state} mode={mode} resolved={resolved} />
);

export default PeriodToolbar;
